import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Bookmark, Book, Trash2, ExternalLink } from 'lucide-react';
import { useAuthStore } from '../../hooks/useStore';

import toast from 'react-hot-toast';
import './Bookmarks.css';

const Bookmarks = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, removeBookmark, fetchUser } = useAuthStore();
  const [bookmarks, setBookmarks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    const loadBookmarks = async () => {
      setIsLoading(true);
      try { await fetchUser(); } finally { setIsLoading(false); }
    };
    loadBookmarks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (user?.bookmarks) {
      setBookmarks(user.bookmarks);
    }
  }, [user]);

  const handleRemove = async (novelId) => {
    try {
      await removeBookmark(novelId);
      setBookmarks(bookmarks.filter(b => b.novel?._id !== novelId && b.novel !== novelId));
      toast.success('Removed from bookmarks');
    } catch (error) {
      toast.error('Something went wrong');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <main className="bookmarks-page">
        <div className="container">
          <div className="loading">
            <div className="loading-spinner"></div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="bookmarks-page">
      <div className="container">
        <div className="page-header">
          <Bookmark size={32} />
          <div>
            <h1>Bookmarked Novels</h1>
            <p>{bookmarks.length} novels in your list</p>
          </div>
        </div>

        {bookmarks.length > 0 ? (
          <div className="bookmarks-list">
            {bookmarks.map((item) => {
              const novel = item.novel;
              if (!novel || typeof novel === 'string') return null;
              
              return (
                <div key={novel._id} className="bookmark-item">
                  <Link to={`/novel/${novel.slug}`} className="bookmark-cover">
                    {novel.cover ? (
                      <img src={novel.cover} alt={novel.title} />
                    ) : (
                      <div className="cover-placeholder">
                        <Book size={24} />
                      </div>
                    )}
                  </Link>
                  
                  <div className="bookmark-info">
                    <Link to={`/novel/${novel.slug}`} className="bookmark-title">
                      {novel.title}
                    </Link>
                    <p className="bookmark-author">Author: {novel.author}</p>
                    <div className="bookmark-meta">
                      <span>{novel.chapterCount} chapters</span>
                      <span>•</span>
                      <span>Bookmarked: {formatDate(item.addedAt)}</span>
                    </div>
                  </div>
                  
                  <div className="bookmark-actions">
                    <Link to={`/novel/${novel.slug}`} className="btn-read">
                      <ExternalLink size={16} />
                      Read
                    </Link>
                    <button 
                      className="btn-remove"
                      onClick={() => handleRemove(novel._id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <Bookmark size={48} />
            <h3>No bookmarked novels yet</h3>
            <p>Bookmark your favorite novels to easily track them</p>
            <Link to="/" className="btn-explore">
              Explore Novels
            </Link>
          </div>
        )}
      </div>
    </main>
  );
};

export default Bookmarks;
